import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRightLeft, AlertTriangle, Search, X, LayoutList, ListCollapse } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { useSelector } from 'react-redux';
import { selectCompanyName } from '@/store/slices/companySlice';
import { usePunchLogs, usePunchDevices } from '@/api/queries/punch-log.queries';
import { useEmployees } from '@/api/queries/employee.queries';
import { StatCard } from '@/components/shared/StatCard';
import { ReportExportBar } from '@/components/reports/ReportExportBar';
import { buildSwipeRows } from '@/lib/ReportGenerator';
import { cn } from '@/lib/cn';

const TYPE_CHIP = {
  IN:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  OUT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

export default function SwipeReportPage() {
  const companyName = useSelector(selectCompanyName);
  const today = format(new Date(), 'yyyy-MM-dd');
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 29), 'yyyy-MM-dd'));
  const [dateTo, setDateTo]     = useState(today);
  const [empSearch, setEmpSearch] = useState('');
  const [deviceFilter, setDevice] = useState('');
  const [typeFilter, setType]     = useState('');
  const [grouped, setGrouped]     = useState(false);

  const { data: empData } = useEmployees({ status: 'ACTIVE', limit: 500 });
  const realEmployees = useMemo(() => empData?.data || [], [empData]);

  const { data: logsData, isLoading } = usePunchLogs({
    date_from: dateFrom, date_to: dateTo,
    ...(deviceFilter ? { device_id: deviceFilter } : {}),
    ...(typeFilter   ? { punch_type: typeFilter }   : {}),
    limit: 1000,
  }, realEmployees);
  const { data: devicesData } = usePunchDevices();

  const devices = devicesData?.data || [];
  const allLogs = useMemo(() => {
    let logs = logsData?.data || [];
    if (empSearch) {
      const q = empSearch.toLowerCase();
      logs = logs.filter(l => l.employee_name?.toLowerCase().includes(q) || l.employee_code?.toLowerCase().includes(q));
    }
    return logs.sort((a, b) => a.punch_time?.localeCompare(b.punch_time));
  }, [logsData, empSearch]);

  // Detect anomalies: employee+date with only 1 punch
  const anomalyKeys = useMemo(() => {
    const map = {};
    allLogs.forEach(l => { const k = `${l.employee_id}|${l.date}`; map[k] = (map[k] || 0) + 1; });
    return new Set(Object.entries(map).filter(([, v]) => v === 1).map(([k]) => k));
  }, [allLogs]);

  // Group by employee for grouped view
  const grouped_data = useMemo(() => {
    if (!grouped) return null;
    const map = {};
    allLogs.forEach(l => {
      const k = `${l.employee_id}|${l.date}`;
      if (!map[k]) map[k] = { ...l, punches: [], firstIn: null, lastOut: null };
      map[k].punches.push(l);
      if (l.punch_type === 'IN' && (!map[k].firstIn || l.punch_time < map[k].firstIn)) map[k].firstIn = l.punch_time;
      if (l.punch_type === 'OUT' && (!map[k].lastOut || l.punch_time > map[k].lastOut)) map[k].lastOut = l.punch_time;
    });
    return Object.values(map).map(row => {
      let workMin = 0;
      if (row.firstIn && row.lastOut) {
        workMin = Math.round((new Date(row.lastOut) - new Date(row.firstIn)) / 60000);
      }
      return { ...row, workMin, isAnomaly: anomalyKeys.has(`${row.employee_id}|${row.date}`) };
    }).sort((a, b) => a.date.localeCompare(b.date) || a.employee_name?.localeCompare(b.employee_name));
  }, [allLogs, grouped, anomalyKeys]);

  const exportRows = useMemo(() => buildSwipeRows(allLogs), [allLogs]);

  const stats = useMemo(() => ({
    total: allLogs.length,
    days:  new Set(allLogs.map(l => l.date)).size,
    anomalies: anomalyKeys.size,
    avgPerDay: allLogs.length && new Set(allLogs.map(l => l.date)).size
      ? (allLogs.length / new Set(allLogs.map(l => l.date)).size).toFixed(1) : 0,
  }), [allLogs, anomalyKeys]);

  const fmtTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) : '—';
  const fmtMins = (m) => m > 0 ? `${Math.floor(m/60)}h ${m%60}m` : '—';

  return (
    <div className="p-6 space-y-5">
      <nav className="flex items-center gap-1.5 text-[12px] text-[var(--text-tertiary)]">
        <Link to="/reports" className="hover:text-[var(--accent)]">Reports</Link>
        <ChevronRight size={12}/>
        <span className="text-[var(--text-primary)] font-medium">Swipe Report</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Swipe Report</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">All biometric punches — {companyName}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setGrouped(v => !v)}
            className={cn('flex items-center gap-2 h-9 px-3 border rounded-lg text-[13px] font-medium transition-all', grouped ? 'border-[var(--accent)] text-[var(--accent)] bg-[var(--accent-muted)]' : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]')}>
            {grouped ? <LayoutList size={14}/> : <ListCollapse size={14}/>}
            {grouped ? 'All Rows' : 'Group by Employee'}
          </button>
          <ReportExportBar rows={exportRows} filename={`swipe-${dateFrom}-${dateTo}`} title="Swipe Report" companyName={companyName} filterSummary={`${dateFrom} to ${dateTo}`}/>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Punches"  value={stats.total.toLocaleString()}     icon={ArrowRightLeft} color="blue"/>
        <StatCard title="Active Days"    value={stats.days.toString()}             icon={ArrowRightLeft} color="green"/>
        <StatCard title="Avg / Day"      value={stats.avgPerDay.toString()}        icon={ArrowRightLeft} color="yellow"/>
        <StatCard title="Anomalies"      value={stats.anomalies.toString()}        icon={AlertTriangle}  color="red"/>
      </div>

      {/* Filters */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 flex flex-wrap gap-3">
        <div className="flex items-center gap-1.5 flex-1 min-w-[180px]">
          <Search size={14} className="text-[var(--text-tertiary)] shrink-0"/>
          <input placeholder="Search employee…" value={empSearch} onChange={e => setEmpSearch(e.target.value)}
            className="flex-1 text-[13px] bg-transparent outline-none placeholder:text-[var(--text-tertiary)]"/>
        </div>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]"/>
        <select value={deviceFilter} onChange={e => setDevice(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">All Devices</option>
          {devices.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setType(e.target.value)}
          className="h-8 px-2.5 text-[12px] bg-[var(--bg-app)] border border-[var(--border)] rounded-lg outline-none focus:border-[var(--accent)]">
          <option value="">IN + OUT</option>
          <option value="IN">IN only</option>
          <option value="OUT">OUT only</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          {grouped ? (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--bg-app)]">
                  {['Date','Employee','Code','Dept','First IN','Last OUT','Punches','Work Hours','Anomaly'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!grouped_data?.length ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-[var(--text-tertiary)]">No data</td></tr>
                ) : grouped_data.map((row, i) => (
                  <tr key={i} className={cn('border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors', row.isAnomaly && 'bg-amber-50/50 dark:bg-amber-900/10')}>
                    <td className="px-4 py-3 font-medium whitespace-nowrap">{row.date}</td>
                    <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">{row.employee_name}</td>
                    <td className="px-4 py-3 font-mono text-[12px] text-[var(--text-tertiary)]">{row.employee_code}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)]">{row.department}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] tabular-nums">{fmtTime(row.firstIn)}</td>
                    <td className="px-4 py-3 text-[var(--text-secondary)] tabular-nums">{fmtTime(row.lastOut)}</td>
                    <td className="px-4 py-3 text-center font-medium">{row.punches.length}</td>
                    <td className="px-4 py-3 tabular-nums text-[var(--text-secondary)]">{fmtMins(row.workMin)}</td>
                    <td className="px-4 py-3">
                      {row.isAnomaly && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                          <AlertTriangle size={10}/> Missing exit
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--bg-app)]">
                  {['Date','Employee','Code','Dept','Time','Type','Device','Source','Status'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-semibold text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b border-[var(--border)]">
                      {Array.from({ length: 9 }).map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-[var(--bg-hover)] rounded animate-pulse w-20"/></td>
                      ))}
                    </tr>
                  ))
                ) : allLogs.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-[var(--text-tertiary)]">No punch data for the selected filters</td></tr>
                ) : allLogs.map((log, i) => {
                  const isAnomaly = anomalyKeys.has(`${log.employee_id}|${log.date}`);
                  return (
                    <tr key={i} className={cn('border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg-hover)] transition-colors', isAnomaly && 'bg-amber-50/50 dark:bg-amber-900/10')}>
                      <td className="px-4 py-3 font-medium whitespace-nowrap text-[var(--text-primary)]">{log.date}</td>
                      <td className="px-4 py-3 font-medium text-[var(--text-primary)] whitespace-nowrap">{log.employee_name}</td>
                      <td className="px-4 py-3 font-mono text-[12px] text-[var(--text-tertiary)]">{log.employee_code}</td>
                      <td className="px-4 py-3 text-[var(--text-secondary)]">{log.department}</td>
                      <td className="px-4 py-3 tabular-nums text-[var(--text-secondary)] whitespace-nowrap">
                        {fmtTime(log.punch_time)}
                        {isAnomaly && <AlertTriangle size={11} className="inline ml-1.5 text-amber-500" title="Only punch — missing exit"/>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold', TYPE_CHIP[log.punch_type] || TYPE_CHIP.IN)}>
                          {log.punch_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[var(--text-secondary)] whitespace-nowrap">{log.device_name}</td>
                      <td className="px-4 py-3 text-[var(--text-tertiary)]">{log.source}</td>
                      <td className="px-4 py-3 text-[var(--text-tertiary)] text-[12px]">{log.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-3 border-t border-[var(--border)] bg-[var(--bg-app)] text-[12px] text-[var(--text-tertiary)]">
          {allLogs.length.toLocaleString()} punches · {stats.anomalies} anomaly days
        </div>
      </div>
    </div>
  );
}
